import React, { useEffect, useState, useRef, useCallback } from "react";
import { X } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const STORAGE = {
  CAPTURED: "azu_lead_captured",       // "1" quando o visitante enviou os dados
  DISMISSED_UNTIL: "azu_lead_dismissed_until", // ISO date até quando não mostrar
  SESSION_SHOWN: "azu_lead_shown_session",     // "1" quando já apareceu nesta sessão
};
const DISMISS_COOLDOWN_DAYS = 7;

function isMobileViewport() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 767px)").matches;
}

function readUTM() {
  const q = new URLSearchParams(window.location.search);
  return {
    utm_source: q.get("utm_source"),
    utm_medium: q.get("utm_medium"),
    utm_campaign: q.get("utm_campaign"),
    utm_term: q.get("utm_term"),
    utm_content: q.get("utm_content"),
    campaign: q.get("campaign") || q.get("utm_campaign"),
  };
}

function shouldShow() {
  try {
    if (localStorage.getItem(STORAGE.CAPTURED) === "1") return false;
    const until = localStorage.getItem(STORAGE.DISMISSED_UNTIL);
    if (until && new Date(until).getTime() > Date.now()) return false;
    if (sessionStorage.getItem(STORAGE.SESSION_SHOWN) === "1") return false;
  } catch { /* storage disabled → default: show */ }
  return true;
}

export default function LeadCaptureBanner() {
  const [visible, setVisible] = useState(false);
  const [variant, setVariant] = useState("timed"); // "timed" | "scroll" | "exit_intent"
  const [form, setForm] = useState({ name: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const cleanupRef = useRef(() => {});

  const openWith = useCallback((v) => {
    if (!shouldShow()) return;
    try { sessionStorage.setItem(STORAGE.SESSION_SHOWN, "1"); } catch {}
    setVariant(v);
    setVisible(true);
    cleanupRef.current(); // remove watchers assim que abre
  }, []);

  useEffect(() => {
    if (!shouldShow()) return;

    // Instant open when the QA flag is present (bypasses timers)
    const qa = new URLSearchParams(window.location.search).get("showLead") === "1";
    if (qa) { openWith("timed"); return; }

    const timerId = setTimeout(() => openWith("timed"), 12000);

    const onScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      if (total > 0 && scrolled / total >= 0.5) openWith("scroll");
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    // Exit intent — apenas desktop
    const desktop = !isMobileViewport();
    const onMouseLeave = (e) => {
      if (e.clientY <= 0) openWith("exit_intent");
    };
    if (desktop) document.addEventListener("mouseleave", onMouseLeave);

    cleanupRef.current = () => {
      clearTimeout(timerId);
      window.removeEventListener("scroll", onScroll);
      if (desktop) document.removeEventListener("mouseleave", onMouseLeave);
    };
    return () => cleanupRef.current();
  }, [openWith]);

  function dismiss() {
    setVisible(false);
    try {
      const until = new Date(Date.now() + DISMISS_COOLDOWN_DAYS * 86400 * 1000).toISOString();
      localStorage.setItem(STORAGE.DISMISSED_UNTIL, until);
    } catch {}
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim() || form.phone.replace(/\D/g, "").length < 8) {
      toast.error("Informe nome e WhatsApp válidos.");
      return;
    }
    setSubmitting(true);
    try {
      const utm = readUTM();
      await api.post("/public/leads", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        source: "lead_captura",
        origin_url: window.location.href,
        device: isMobileViewport() ? "mobile" : "desktop",
        variant,
        ...utm,
      });
      try { localStorage.setItem(STORAGE.CAPTURED, "1"); } catch {}
      toast.success("Recebemos seu contato. Falaremos em breve.");
      setVisible(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível enviar. Tente pelo WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!visible) return null;

  const isExitIntent = variant === "exit_intent";
  const headline = isExitIntent ? "Antes de sair…" : "Receba as próximas oportunidades";
  const subline = isExitIntent
    ? "Receba primeiro as novas casas disponíveis."
    : "Casas selecionadas em Pernambuco, antes de qualquer plataforma.";

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Convite para receber novidades"
      data-testid="lead-capture-banner"
      className="fixed z-40 bottom-4 right-4 left-4 md:left-auto md:bottom-6 md:right-6 md:max-w-sm bg-white/95 backdrop-blur-md border border-[#E9F5F3] rounded-2xl shadow-[0_18px_45px_-18px_rgba(10,168,156,0.35)] p-5 md:p-6 animate-in fade-in slide-in-from-bottom-4 duration-500"
    >
      <button
        type="button"
        onClick={dismiss}
        data-testid="lead-capture-close"
        aria-label="Fechar"
        className="absolute top-3 right-3 w-8 h-8 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="text-[10px] uppercase tracking-[0.28em] text-[#066B63] font-semibold flex items-center gap-2">
        <span className="w-4 h-px bg-[#066B63]/60" aria-hidden="true" />
        Azuhomes
      </div>
      <h3 className="font-display text-lg md:text-xl text-[#22302E] mt-2 leading-tight">
        {headline}
      </h3>
      <p className="text-sm text-[#22302E]/70 mt-1 leading-relaxed">
        {subline}
      </p>

      <form onSubmit={submit} className="mt-4 space-y-2.5">
        <input
          type="text"
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Seu nome"
          data-testid="lead-capture-name"
          className="w-full px-3 py-2.5 text-sm rounded-md border border-slate-200 focus:border-[#0AA89C] focus:ring-1 focus:ring-[#0AA89C]/40 outline-none transition-colors"
        />
        <input
          type="tel"
          required
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          placeholder="WhatsApp com DDD"
          data-testid="lead-capture-phone"
          inputMode="tel"
          className="w-full px-3 py-2.5 text-sm rounded-md border border-slate-200 focus:border-[#0AA89C] focus:ring-1 focus:ring-[#0AA89C]/40 outline-none transition-colors"
        />
        <button
          type="submit"
          disabled={submitting}
          data-testid="lead-capture-submit"
          className="w-full mt-1 px-4 py-2.5 bg-[#0AA89C] hover:bg-[#088c82] text-white text-sm font-medium rounded-md transition-colors disabled:opacity-60"
        >
          {submitting ? "Enviando…" : "Quero receber"}
        </button>
      </form>

      <p className="text-[10px] text-slate-400 mt-3 leading-relaxed">
        Ao enviar, você autoriza o contato pela Azuhomes conforme nossa{" "}
        <a href="/politica-privacidade" className="underline hover:text-[#066B63]">política de privacidade</a>.
      </p>
    </div>
  );
}
